import type { ComponentType } from "react";
import type { GuideBlockProps } from "./types";

/**
 * How a block looks once its step is done (#436 done states).
 *
 * - `hide`: the block is a form or an action. A done step collapses to a short
 *   ✓ summary; "Change this" brings the form back (for this visit only).
 * - `readonly`: the block shows information (a map, Wi-Fi details, a link).
 *   It stays visible as normal, with the Done badge.
 */
export type BlockDoneMode = "hide" | "readonly";

export interface BlockDoneMeta {
  doneMode: BlockDoneMode;
  /** One short, plain sentence for a collapsed done step. Members' English
   *  may be weak: keep it simple. */
  summary?: string;
  /** Optional: a small live view shown instead of `summary` (e.g. "PIN set
   *  on 3 May"). Gets the same props as the block. */
  DoneView?: ComponentType<GuideBlockProps>;
}

/** Used for a block with no entry below: most blocks are forms, so hide. */
export const DEFAULT_DONE_META: BlockDoneMeta = { doneMode: "hide" };

/** What a collapsed step says when its block gives no summary. */
export const DEFAULT_DONE_SUMMARY = "You have done this step.";

/** Under the summary when the step's own text is hidden with the form. */
export const HIDDEN_TEXT_NOTE = "The text for this step is hidden too.";

/**
 * Per-block done behaviour, by block key. Optional: a new block works without
 * an entry (it gets `DEFAULT_DONE_META`). Add one to show its content when done
 * (`readonly`) or to give a better summary.
 */
const DONE_META: Partial<Record<string, BlockDoneMeta>> = {
  // Forms and actions: hidden when done.
  "wifi.personal_credential": { doneMode: "hide", summary: "You have your own Wi-Fi login. To use it on another device, press Change this." },
  "wifi.change_password": { doneMode: "hide", summary: "Your new Wi-Fi password is saved. It starts working tonight." },
  "door.pin": { doneMode: "hide", summary: "Your door PIN is set." },
  "print.start": { doneMode: "hide", summary: "We sent you an email to set up printing." },
  "network.add_device": { doneMode: "hide", summary: "Done. To add a device by its MAC address, press Change this." },
  "company.booking_approval": { doneMode: "hide", summary: "The rule for booking approval is set." },
  "company.opening_hours": { doneMode: "hide", summary: "Your opening hours are set." },
  "company.billing": { doneMode: "hide", summary: "Your billing details are saved." },
  "colleagues.invite": { doneMode: "hide", summary: "You invited your colleagues." },
  "slack.install": { doneMode: "hide", summary: "Slack is connected." },
  "doorbell.chime": { doneMode: "hide", summary: "Your doorbell sound is set." },
  "doorbell.call_settings": { doneMode: "hide", summary: "Live calls are on for your company." },
  "doorbell.call_alerts": { doneMode: "hide", summary: "Notifications are on for this device." },
  "doorbell.ring_me": { doneMode: "hide", summary: "You answered a test call." },
  // The quiz: passed. "Change this" shows the questions again.
  "guide.quiz": { doneMode: "hide", summary: "You passed the quiz." },
  // Information: stays visible when done.
  "floorplan.location": { doneMode: "readonly" },
  "wifi.network_info": { doneMode: "readonly" },
  "portal.link": { doneMode: "readonly" },
  "meeting.find_room": { doneMode: "readonly" },
  // Landing from parallel branches (alarm guide !1237, Company network guide):
  // listed ahead of their keys, so they never fall back to the default.
  "alarm.schedule": { doneMode: "readonly" },
  "network.device_list": { doneMode: "readonly" },
  // Display-only (the plan and its capacity); changing the plan is elsewhere.
  "network.plan_capacity": { doneMode: "readonly" },
};

/** Block keys with an explicit done entry (a test checks every known key has one). */
export function hasDoneEntry(blockKey: string): boolean {
  return Object.prototype.hasOwnProperty.call(DONE_META, blockKey);
}

export function blockDoneMeta(blockKey: string): BlockDoneMeta {
  return (Object.prototype.hasOwnProperty.call(DONE_META, blockKey) && DONE_META[blockKey]) || DEFAULT_DONE_META;
}

export function blockDoneMode(blockKey: string): BlockDoneMode {
  return blockDoneMeta(blockKey).doneMode;
}
